import React, { createContext, useContext } from 'react';
import type {
  PopoutWidgetData,
  PopoutWindowOptions,
} from '~/hooks/usePopoutWindow';
import { usePopoutWindow } from '~/hooks/usePopoutWindow';

interface PopoutContextType {
  openPopout: (
    widgetId: string,
    widgetData: PopoutWidgetData,
    options?: PopoutWindowOptions,
  ) => void;
  openWidgetPopout: (
    widgetComponent: React.ComponentType<any>,
    props: Record<string, any>,
    options?: PopoutWindowOptions & { widgetId: string },
  ) => void;
  closePopout: (widgetId: string) => void;
  isPopoutOpen: (widgetId: string) => boolean;
}

const PopoutContext = createContext<PopoutContextType | undefined>(undefined);

export const PopoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const popoutWindow = usePopoutWindow();

  return (
    <PopoutContext.Provider value={popoutWindow}>
      {children}
    </PopoutContext.Provider>
  );
};

export const usePopout = () => {
  const context = useContext(PopoutContext);
  if (context === undefined) {
    // Return a no-op implementation when used outside provider (e.g., inside a popout)
    return {
      openPopout: () => {
        console.warn(
          'openPopout called from within a popout window - ignoring',
        );
      },
      openWidgetPopout: () => {
        console.warn(
          'openWidgetPopout called from within a popout window - ignoring',
        );
      },
      closePopout: () => {
        // No-op when called from within a popout
      },
      isPopoutOpen: () => false,
    };
  }
  return context;
};
